import type { RuleSet } from 'styled-components';

import { memo } from 'react';
import isEqual from 'react-fast-compare';
import styled, { css } from 'styled-components';

import type { ProductCountdownTimer } from '~/amplienceTypes/schemas/imported/product-countdown-timer-schema';
import { CountdownTimer } from '~/components/pdp/countdownTimer/CountdownTimer';
import { Typography } from '~/components/shared/core/typography/Typography';
import type { StockLevel, StockLevelMessage } from '~/utilities/graphql/codegen';
import { StockLevelMessageKey, SiteType } from '~/utilities/graphql/codegen';

const GstarStockStyle = css`
  margin-top: ${({ theme }) => theme.spacing.y.xs};
  margin-bottom: 32px;
`;

const AafStockStyle = css`
  margin: 12px 0 ${({ theme }) => theme.spacing.x.xs};
`;

const StockConfig: Record<SiteType, RuleSet> = {
  [SiteType.Gstar]: GstarStockStyle,
  [SiteType.Wahts]: GstarStockStyle,
  [SiteType.Outlet]: GstarStockStyle,
  [SiteType.Employeeshop]: GstarStockStyle,
  [SiteType.Aaf]: AafStockStyle,
};
const S = {
  Stock: styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    ${({ theme }) => StockConfig[theme.siteType]};

    /* No message when in stock. As a block, the empty wrapper's margins collapse with the
       size selector's, so only its bottom margin remains */
    &:empty {
      display: block;
    }
  `,
  StockAvailability: styled(Typography)`
    font-weight: ${({ theme }) => theme.variables.font.weight.regular};
    font-size: ${({ theme }) => theme.font.size[2]};
    line-height: 16px;
    color: ${({ theme }) => theme.colors.content.warning};
  `,
};

interface Props {
  stockLevelMessage: StockLevelMessage;
  countdownTimerContent?: ProductCountdownTimer;
  stockInformation?: StockLevel | null;
  salesDiscountsEndDate?: string | null;
}

const StockAvailabilityMessageComponent = ({
  stockInformation,
  stockLevelMessage,
  countdownTimerContent,
  salesDiscountsEndDate,
}: Props) => (
  <>
    {/* Rendered even when in stock, so its margin keeps the sizes apart from the add to bag CTA */}
    <S.Stock>
      {stockLevelMessage.key !== StockLevelMessageKey.Plenty && (
        <S.StockAvailability
          component="p"
          variant="title"
          testId="stock-level-notification"
          data-cs-capture="true"
        >
          {stockLevelMessage.value}
        </S.StockAvailability>
      )}
    </S.Stock>
    {stockInformation?.stockQuantity !== 0 && countdownTimerContent && (
      <CountdownTimer {...countdownTimerContent} salesDiscountsEndDate={salesDiscountsEndDate} />
    )}
  </>
);

export const StockAvailabilityMessage = memo(StockAvailabilityMessageComponent, isEqual);
